import { Badge } from '@repo/ui/badge'
import { DataTable } from '@repo/ui/commons/data-table/data-table'
import type { DataTableColumn } from '@repo/ui/commons/data-table/types'
import {
	postBackofficeAuthGetSession,
	postBackofficeAuthListAdmins
} from '@repo/sdk/gen'
import { useQuery } from '@tanstack/react-query'
import { createFileRoute, useNavigate } from '@tanstack/react-router'
import { ShieldCheck } from 'lucide-react'
import { z } from 'zod'
import { BackofficeShell } from '@/components/backoffice-shell'
import { QueryErrorState } from '@/components/query-error-state'
import { useTranslation } from '@/lib/i18n'
import { requireBackofficeSession } from '@/lib/require-backoffice-session'
import { DebouncedSearchInput } from './-components/debounced-search-input'
import { EditAdminAbilitiesDialog } from './-components/edit-admin-abilities-dialog'
import { FilterSelect } from './-components/filter-select'
import { InviteAdminDialog } from './-components/invite-admin-dialog'
import { ResetFiltersButton } from './-components/reset-filters-button'

const ADMIN_STATUSES = ['all', 'active', 'disabled', 'pending'] as const

export const Route = createFileRoute('/admins')({
	ssr: false,
	beforeLoad: requireBackofficeSession,
	validateSearch: z.object({
		direction: z.enum(['asc', 'desc']).optional(),
		page: z.coerce.number().int().positive().optional(),
		q: z.string().optional(),
		sortBy: z.enum(['createdAt', 'name']).optional(),
		status: z.enum(['all', 'active', 'disabled', 'pending']).optional()
	}),
	component: AdminsPage
})

function AdminsPage() {
	const { t } = useTranslation()
	const search = Route.useSearch()
	const navigate = useNavigate()
	const page = search.page ?? 1
	const sortBy = search.sortBy ?? 'createdAt'
	const direction = search.direction ?? 'desc'
	const adminsQuery = useQuery({
		queryKey: ['backoffice', 'admins', search],
		queryFn: () =>
			postBackofficeAuthListAdmins({
				body: {
					filters: {
						...(search.q ? { search: search.q } : {}),
						...(search.status && search.status !== 'all'
							? { status: search.status }
							: {})
					},
					limit: 50,
					page,
					sort: { by: sortBy, direction }
				}
			}).then((response) => response.data)
	})
	const sessionQuery = useQuery({
		queryKey: ['auth', 'session'],
		queryFn: () =>
			postBackofficeAuthGetSession().then((response) => response.data)
	})
	const data = adminsQuery.data?.data ?? []
	const hasActiveFilters = Boolean(
		search.q || (search.status && search.status !== 'all')
	)
	const columns: DataTableColumn<(typeof data)[number], typeof sortBy>[] = [
		{
			id: 'name',
			header: t('common:administrator'),
			cell: (item) => (
				<div className='flex items-center gap-3'>
					<div className='grid size-9 place-items-center rounded-full bg-primary/10 text-primary'>
						<ShieldCheck className='size-4' />
					</div>
					<div className='min-w-0'>
						<p className='truncate font-medium'>
							{item.name ??
								item.email ??
								t('common:unnamed_administrator')}
						</p>
						<p className='truncate text-muted-foreground text-xs'>
							{item.email ?? '—'}
						</p>
					</div>
				</div>
			),
			sortKey: 'name',
			flex: 1
		},
		{
			id: 'abilities',
			header: t('common:abilities'),
			cell: (item) => (
				<div className='flex flex-wrap gap-1'>
					{item.abilities.map((ability) => (
						<Badge key={ability} variant='secondary'>
							{ability}
						</Badge>
					))}
				</div>
			),
			width: 360
		},
		{
			id: 'status',
			header: t('common:status'),
			cell: (item) => (
				<Badge
					variant={
						item.status === 'disabled' ? 'destructive' : 'outline'
					}
				>
					{item.status}
				</Badge>
			),
			width: 130
		},
		{
			id: 'actions',
			header: t('common:actions'),
			cell: (item) => (
				<div className='flex justify-end'>
					<EditAdminAbilitiesDialog
						admin={item}
						disabled={sessionQuery.data?.admin?.id === item.id}
					/>
				</div>
			),
			pinned: 'right',
			width: 180,
			minWidth: 180,
			maxWidth: 180,
			enableResizing: false
		}
	]

	function updateSearch(next: Partial<typeof search>) {
		navigate({ to: Route.fullPath, search: { ...search, ...next } })
	}

	return (
		<BackofficeShell contentClassName='max-w-none'>
			<header className='flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between'>
				<div>
					<p className='font-medium text-primary text-sm'>
						{t('common:backoffice')}
					</p>
					<h1 className='mt-2 font-semibold text-3xl tracking-tight'>
						{t('common:administrators')}
					</h1>
				</div>
				<div>
					<InviteAdminDialog />
				</div>
			</header>
			<div className='mt-6 flex flex-wrap gap-3'>
				<DebouncedSearchInput
					ariaLabel={t('common:search')}
					className='w-full sm:w-60'
					onValueChange={(q) =>
						updateSearch({ page: 1, q: q || undefined })
					}
					placeholder={t('common:search')}
					value={search.q ?? ''}
				/>
				<FilterSelect
					ariaLabel={t('common:filter_by_status')}
					items={[
						{ label: t('common:all_statuses'), value: 'all' },
						{ label: t('common:active'), value: 'active' },
						{ label: t('common:disabled'), value: 'disabled' },
						{ label: t('common:pending'), value: 'pending' }
					]}
					label={t('common:status')}
					onValueChange={(value) =>
						updateSearch({
							page: 1,
							status:
								value === 'all'
									? undefined
									: ADMIN_STATUSES.find(
											(item) => item === value
										)
						})
					}
					value={search.status ?? 'all'}
				/>
				<ResetFiltersButton
					disabled={!hasActiveFilters}
					onClick={() =>
						updateSearch({
							page: 1,
							q: undefined,
							status: undefined
						})
					}
				/>
			</div>
			<DataTable
				className='mt-6 h-[calc(100vh-17rem)] min-h-96'
				columns={columns}
				data={data}
				pagination={{
					onPageChange: (pageIndex) =>
						updateSearch({ page: pageIndex + 1 }),
					pageCount: adminsQuery.data?.meta.totalPages ?? 0,
					pageIndex: page - 1
				}}
				sort={{
					onSortChange: (next) =>
						updateSearch({
							direction: next.direction,
							page: 1,
							sortBy: next.by ?? 'createdAt'
						}),
					state: { by: sortBy, direction }
				}}
				state={{
					empty: t('common:empty_administrators'),
					error: adminsQuery.isError ? (
						<QueryErrorState
							message={t('common:administrators_load_error')}
							onRetry={() => adminsQuery.refetch()}
						/>
					) : (
						false
					),
					loading: adminsQuery.isPending
				}}
			/>
		</BackofficeShell>
	)
}
